import { FC, Fragment, useEffect, useRef } from "react";
import {
    Flex,
    Picture,
    Box,
    Spacer,
    PictureProps,
    useBreakpointValue,
} from "@smile2impress/components/relaunch";
import { keyframes } from "@smile2impress/components";
import { useUTSLocaleContext } from "context/utsLocaleContext";

import { AwardsProps } from "./Awards.types";
import content from "./content";

export const Awards: FC<AwardsProps> = ({
    noPX = false,
    runningLine = false,
}) => {
    const { locale } = useUTSLocaleContext();
    const contentData = content(locale)();
    const contentSize = contentData.length;

    const isMobile = useBreakpointValue({
        base: true,
        tablet: true,
        desktop: false,
    });

    const mobileElSize = `${100 / 3}%`;
    const desktopElSize = `${100 / 6}%`;

    const runningLineRef = useRef<HTMLDivElement | null>(null);
    const animationTime = `${(contentSize / 3) * 10}s`;
    const mobileScroll = keyframes`
        to {
            transform: translateX(calc(-1 * ${contentSize} * ${mobileElSize})) translateZ(0);
        }
    `;
    const desktopScroll = keyframes`  
        to {
            transform: translateX(calc(-1 * ${contentSize} * ${desktopElSize})) translateZ(0);
        }
    `;

    const runLine = () => {
        const children = runningLineRef?.current?.children;
        if (children?.length) {
            Array.from(children).forEach((el) => {
                const cloned = el.cloneNode(true);
                runningLineRef?.current?.appendChild(cloned);
            });
        }
    };

    useEffect(() => {
        if (runningLineRef?.current) {
            runLine();
        }
    }, [runningLineRef]);

    if (runningLine)
        return (
            <Box overflow="hidden">
                <Box
                    {...(!noPX && { px: [0, 0, 4] })}
                    whiteSpace="nowrap"
                    animation={[
                        `${mobileScroll} ${animationTime} forwards linear infinite`,
                        `${mobileScroll} ${animationTime} forwards linear infinite`,
                        `${desktopScroll} ${animationTime} forwards linear infinite`,
                    ]}
                    ref={runningLineRef}
                >
                    {contentData.map((item: PictureProps, index: number) => (
                        <Box
                            key={index}
                            display="inline-block"
                            height={["42px", "42px", "105px"]}
                            px={2}
                            width={[mobileElSize, mobileElSize, desktopElSize]}
                            textAlign="center"
                        >
                            <Box h="100%">
                                <Picture
                                    {...item}
                                    height="auto"
                                    width="100%"
                                    fit="contain"
                                />
                            </Box>
                        </Box>
                    ))}
                </Box>
            </Box>
        );

    return (
        <Flex
            {...(!noPX && { px: [0, 0, 4] })}
            justifyContent={["normal", "normal", "space-between"]}
            flexWrap={["wrap", "wrap", "nowrap"]}
            rowGap={[2, 2, 0]}
            alignItems="stretch"
            basis="100%"
        >
            {contentData.map((item: PictureProps, index: number) => (
                <Fragment key={index}>
                    {index > 0 && !isMobile && <Spacer flex="1 1 auto" />}
                    <Flex
                        key={index}
                        maxH={["42px", "42px", "105px"]}
                        width={["max-content"]}
                        textAlign="center"
                        px={[0.5, 0.5, 0]}
                        flexBasis={[mobileElSize, mobileElSize, "auto"]}
                    >
                        <Box width="100%">
                            <Picture
                                {...item}
                                display="inline-block"
                                height={["42px", "42px", "105px"]}
                                width="auto"
                                fit="contain"
                            />
                        </Box>
                    </Flex>
                </Fragment>
            ))}
        </Flex>
    );
};
